import { useState, MouseEvent, useRef, useCallback } from 'react';

// --- IMPORT STANDARD DA NPM ---
import { Document, Page, pdfjs } from 'react-pdf';

// --- CONFIGURAZIONE WORKER PDF.JS ---
//pdfjs.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/pdf.worker.min.mjs';

// --- TIPI E INTERFACCE ---
export interface Coords {
    x: number;
    y: number;
    width: number;
    height: number;
}
interface Dimensions {
    width: number;
    height: number;
}
interface TemplateUploaderProps {
    onTemplateDefined: (templateFile: File, coordsInPoints: Coords) => void;
}
type InteractionMode = 'idle' | 'drawing' | 'moving' | 'resizing';
type ResizeHandle = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';


const PDF_DISPLAY_WIDTH = 800;

const TemplateUploader: React.FC<TemplateUploaderProps> = ({ onTemplateDefined }) => {
    // --- STATI E RIFERIMENTI ---
    const [file, setFile] = useState<File | null>(null);
    const [selectionPixels, setSelectionPixels] = useState<Coords | null>(null);
    const [pdfPageDimensions, setPdfPageDimensions] = useState<Dimensions | null>(null);
    const [displayDimensions, setDisplayDimensions] = useState<Dimensions | null>(null);

    // --- STATI PER L'INTERAZIONE ---
    const [mode, setMode] = useState<InteractionMode>('idle');
    const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
    const [activeResizeHandle, setActiveResizeHandle] = useState<ResizeHandle | null>(null);

    // --- NUOVI STATI PER LA GRIGLIA ---
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const [gridSize, setGridSize] = useState<number>(20); // Dimensione in pixel

    const pdfContainerRef = useRef<HTMLDivElement>(null);

    // --- FUNZIONI DI GESTIONE FILE E PAGINA (INVARIATE) ---
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setFile(event.target.files[0]);
            setSelectionPixels(null);
            setPdfPageDimensions(null);
            setDisplayDimensions(null);
        }
    };

    const onPageLoadSuccess = useCallback((page: any) => {
        setPdfPageDimensions({ width: page.originalWidth, height: page.originalHeight });
    }, []);

    const onPageRenderSuccess = useCallback(() => {
        if (pdfContainerRef.current) {
            const { width, height } = pdfContainerRef.current.getBoundingClientRect();
            setDisplayDimensions({ width, height });
        }
    }, []);

    // --- GESTIONE DEGLI EVENTI MOUSE (INVARIATA) ---
    const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        if (selectionPixels) return;
        e.preventDefault();
        setMode('drawing');
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setStartPoint({ x, y });
        setSelectionPixels({ x, y, width: 0, height: 0 });
    };

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (mode === 'idle' || !pdfContainerRef.current) return;
        const rect = pdfContainerRef.current.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        switch (mode) {
            case 'drawing':
                if (!startPoint) return;
                const deltaX = currentX - startPoint.x;
                const deltaY = currentY - startPoint.y;
                const side = Math.max(Math.abs(deltaX), Math.abs(deltaY));
                const newX = deltaX >= 0 ? startPoint.x : startPoint.x - side;
                const newY = deltaY >= 0 ? startPoint.y : startPoint.y - side;
                setSelectionPixels({ x: newX, y: newY, width: side, height: side });
                break;
            case 'moving':
                if (!dragOffset || !selectionPixels) return;
                const boundedX = Math.max(0, Math.min(currentX - dragOffset.x, rect.width - selectionPixels.width));
                const boundedY = Math.max(0, Math.min(currentY - dragOffset.y, rect.height - selectionPixels.height));
                setSelectionPixels({ ...selectionPixels, x: boundedX, y: boundedY });
                break;
            case 'resizing':
                if (!activeResizeHandle || !selectionPixels) return;
                let { x, y, width } = selectionPixels;
                let newSide = 0;
                if (activeResizeHandle === 'bottom-right') {
                    newSide = Math.max(currentX - x, currentY - y);
                } else if (activeResizeHandle === 'bottom-left') {
                    const oppositeCornerX = x + width;
                    newSide = Math.max(oppositeCornerX - currentX, currentY - y);
                    x = oppositeCornerX - newSide;
                } else if (activeResizeHandle === 'top-right') {
                    const oppositeCornerY = y + width;
                    newSide = Math.max(currentX - x, oppositeCornerY - currentY);
                    y = oppositeCornerY - newSide;
                } else if (activeResizeHandle === 'top-left') {
                    const oppositeCornerX = x + width;
                    const oppositeCornerY = y + width;
                    newSide = Math.max(oppositeCornerX - currentX, oppositeCornerY - currentY);
                    x = oppositeCornerX - newSide;
                    y = oppositeCornerY - newSide;
                }
                newSide = Math.max(10, newSide);
                setSelectionPixels({ x, y, width: newSide, height: newSide });
                break;
        }
    };

    const handleMouseUp = () => {
        setMode('idle');
        setStartPoint(null);
        setDragOffset(null);
        setActiveResizeHandle(null);
    };

    const onSelectionMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!selectionPixels) return;
        setMode('moving');
        const rect = e.currentTarget.getBoundingClientRect();
        setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const onResizeHandleMouseDown = (e: MouseEvent<HTMLDivElement>, handle: ResizeHandle) => {
        e.stopPropagation();
        setMode('resizing');
        setActiveResizeHandle(handle);
    };

    const handleDeleteSelection = () => {
        setSelectionPixels(null);
    };

    const handleSubmit = () => {
        if (!file || !selectionPixels || !pdfPageDimensions || !displayDimensions) {
            alert("Per favore, carica un file PDF e seleziona un'area.");
            return;
        }
        const scaleX = pdfPageDimensions.width / displayDimensions.width;
        const scaleY = pdfPageDimensions.height / displayDimensions.height;
        const finalCoordsInPoints: Coords = {
            x: selectionPixels.x * scaleX,
            y: selectionPixels.y * scaleY,
            width: selectionPixels.width * scaleX,
            height: selectionPixels.height * scaleY,
        };
        onTemplateDefined(file, finalCoordsInPoints);
    };

    // --- NUOVO: Stile dinamico per la griglia ---
    const gridStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `
            linear-gradient(to right, rgba(0, 0, 0, 0.2) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.2) 1px, transparent 1px)
        `,
        backgroundSize: `${gridSize}px ${gridSize}px`,
        pointerEvents: 'none', // FONDAMENTALE: permette i click attraverso la griglia
        zIndex: 1, // Assicura che la griglia sia sopra il PDF ma sotto il resto
    };

    return (
        <div style={{ fontFamily: 'sans-serif' }}>
            <p className="text-slate-400 mb-4">
                { !selectionPixels
                    ? "Carica un PDF e disegna un'area quadrata dove posizionare i QR code."
                    : "Sposta, ridimensiona o elimina l'area, poi salva il modello."
                }
            </p>
            <input type="file" accept="application/pdf" onChange={handleFileChange} className="mb-4 text-slate-300" />

            {/* --- NUOVO: Controlli per la griglia --- */}
            {file && (
                <div className="mb-4 p-2 border border-slate-700 rounded-md flex items-center space-x-4 text-slate-300">
                    <label htmlFor="showGrid" className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            id="showGrid"
                            checked={showGrid}
                            onChange={(e) => setShowGrid(e.target.checked)}
                            className="mr-2"
                        />
                        Mostra Griglia
                    </label>
                    <label htmlFor="gridSize" className="flex items-center">
                        Dimensione (px):
                        <input
                            type="number"
                            id="gridSize"
                            value={gridSize}
                            onChange={(e) => setGridSize(Math.max(5, parseInt(e.target.value, 10) || 5))}
                            className="ml-2 w-20 bg-slate-800 text-white p-1 rounded-md"
                            disabled={!showGrid}
                        />
                    </label>
                </div>
            )}

            {file && (
                <div
                    ref={pdfContainerRef}
                    style={{ position: 'relative', width: 'fit-content', cursor: selectionPixels ? 'default' : 'crosshair' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <Document file={file}>
                        <Page pageNumber={1} width={PDF_DISPLAY_WIDTH} onLoadSuccess={onPageLoadSuccess}
                              onRenderSuccess={onPageRenderSuccess} renderAnnotationLayer={false} renderTextLayer={false}/>
                    </Document>

                    {/* --- NUOVO: Renderizza la griglia se attiva --- */}
                    {showGrid && <div style={gridStyle} />}

                    {selectionPixels && selectionPixels.width > 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                border: '2px dashed #007bff',
                                backgroundColor: 'rgba(0, 123, 255, 0.3)',
                                left: `${selectionPixels.x}px`,
                                top: `${selectionPixels.y}px`,
                                width: `${selectionPixels.width}px`,
                                height: `${selectionPixels.height}px`,
                                cursor: 'move',
                                zIndex: 2, // Posizionato sopra la griglia
                            }}
                            onMouseDown={onSelectionMouseDown}
                        >
                            <div onMouseDown={(e) => onResizeHandleMouseDown(e, 'top-left')} style={resizeHandleStyle('top-left')} />
                            <div onMouseDown={(e) => onResizeHandleMouseDown(e, 'top-right')} style={resizeHandleStyle('top-right')} />
                            <div onMouseDown={(e) => onResizeHandleMouseDown(e, 'bottom-left')} style={resizeHandleStyle('bottom-left')} />
                            <div onMouseDown={(e) => onResizeHandleMouseDown(e, 'bottom-right')} style={resizeHandleStyle('bottom-right')} />
                        </div>
                    )}
                </div>
            )}
            {file && (
                <div className="mt-4">
                    <button onClick={handleSubmit} disabled={!selectionPixels || selectionPixels.width === 0}
                            className="px-6 py-2 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-500 disabled:bg-slate-600">
                        Salva Modello e Area
                    </button>
                    {selectionPixels && (
                        <button onClick={handleDeleteSelection}
                                className="ml-4 px-6 py-2 rounded-lg text-white font-semibold bg-red-600 hover:bg-red-500">
                            Elimina Selezione
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Modificata per aggiungere zIndex
const resizeHandleStyle = (handle: ResizeHandle): React.CSSProperties => {
    const style: React.CSSProperties = {
        position: 'absolute',
        width: '10px',
        height: '10px',
        backgroundColor: '#007bff',
        border: '1px solid white',
        zIndex: 3, // Sopra tutto il resto
    };
    if (handle.includes('top')) style.top = '-5px';
    if (handle.includes('bottom')) style.bottom = '-5px';
    if (handle.includes('left')) style.left = '-5px';
    if (handle.includes('right')) style.right = '-5px';

    if (handle === 'top-left' || handle === 'bottom-right') style.cursor = 'nwse-resize';
    if (handle === 'top-right' || handle === 'bottom-left') style.cursor = 'nesw-resize';

    return style;
};

export default TemplateUploader;